import { Component, OnInit, Inject } from '@angular/core';
import { PolicyClient, UserPreferenceRequest, UserClient, ContactInfo } from '../../api/benefits-clients';
import { BehaviorSubject } from 'rxjs';
import { UserContext } from '../../services/user-context/user-context.types';
import { UserContextService } from '../../services/user-context/user-context.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DataLabel } from '@benefitsui/app/shared/interfaces/IDataLabel';
import { Clipboard } from '@angular/cdk/clipboard';
import { WINDOW } from '@benefitsui/app/lib/tokens/window';
import { environment } from '@benefitsui/environments/environment';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';
import { ladManagerCard } from '../contact-us/contacts-page.component';
import { EnvName } from 'envName';
import { UserRole } from '../../shared/enums/users';

declare global {
  interface QsiInterface {
    API: {
      unload: () => void;
      load: () => Promise<void>;
      run: () => PromiseLike<void> | void | null | undefined;
    };
  }

  interface Window {
    QSI?: QsiInterface;
  }
}
@Component({
  selector: 'app-dashboard-page',
  templateUrl: './dashboard-page.component.html',
  styleUrls: ['./dashboard-page.component.scss'],
  standalone: false,
  providers: [UserContextService]
})
export class DashboardPageComponent implements OnInit {
  constructor(
    private userContextService: UserContextService,
    private policyClient: PolicyClient,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private clipboard: Clipboard,
    @Inject(WINDOW) private window: Window,
    private userClient: UserClient
  ) {
    this.userContext = this.userContextService.get();
    this.userFirstName = this.userContext.currentUserFirstName;
    // Only show the invoice notifications toggle if the user is a sponsor and an admin
    this.showToggle =
      this.userContext.symClass?.toLowerCase() === 'sponsor' && this.userContext.policyholderInfo?.isAdmin === true;
    // show the survey card if the user is a sponsor or broker in non-demo environments
    this.displaySurveyCard =
      environment.environmentName !== EnvName.DEMO &&
      environment.environmentName !== EnvName.DEMODEV &&
      (this.userContext.symClass?.toLowerCase() === UserRole.Sponsor ||
        this.userContext.symClass?.toLowerCase() === UserRole.Broker);
  }

  //constants
  MissingContactText = 'Not seeing who you need?';
  ContactUs = 'Contact us now';
  Welcome = 'Welcome';
  Group = 'Group';
  HeroText1 = 'Introducing our new';
  HeroText2 = 'Benefits Portal';
  HeroText3 = 'More features coming soon. Check back regularly for exciting updates.';
  HeroAltText =
    'Introducing our new Benefits Portal! More features coming next year. Check back regularly for exciting updates to your experience.';
  quickLinkIcon = 'open_in_new';
  copyIcon = 'content_copy';
  checkIcon = 'check';
  EmailCopyText = 'Copied to clipboard!';
  employerQuickLink = {
    title: "Symetra's Benefits products",
    text: 'Visit Symetra.com to explore our products for employers',
    link: 'https://www.symetra.com/our-products/employers'
  };
  employeeQuickLink = {
    title: 'Employee education & knowledge',
    text: 'Visit Symetra.com for employee education resources',
    link: 'https://www.symetra.com/our-products/for-employees'
  };
  podcastQuickLink = {
    title: 'Working Forward Podcast',
    text: 'Visit our podcast to watch new episodes',
    link: 'https://www.symetra.com/our-products/employers/working-forward-podcast'
  };
  accountManager = 'Account Manager';
  token = '';
  userContext: UserContext;
  userFirstName: string;
  overviewData!: OverviewData;
  contactData!: ContactData[];
  isLoading = true;
  hasContacts = true;
  hasInterceptLoaded = false;
  displaySurveyCard = false;
  invoiceNotificationsEnabled = false;
  InvoiceNotificationsText = 'Invoice email notifications';
  quickLinksHeader = 'Helpful quick-links';
  surveyBodyHeader = 'Your opinion matters to us!';
  contactsBodyHeader = 'Symetra contacts';
  settingsBodyHeader = 'Settings';
  showToggle = false;
  formatDate(date: string | undefined): string {
    if (!date) {
      return '';
    }
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString();
  }

  toggleInvoiceNotifications(isEnabled: boolean) {
    const preferenceRequest: UserPreferenceRequest = {
      invoiceNotification: isEnabled,
      groupNumber: this.overviewData?.policyNumber
    };

    this.userClient.preferencePost(preferenceRequest).subscribe({
      next: () => {
        this.invoiceNotificationsEnabled = isEnabled;
      },
      error: (error) => {
        console.error('Error setting user preference:', error);
      }
    });
  }

  copyEmail(email: string, index: number) {
    const pending = this.clipboard.beginCopy(email);
    let remainingAttempts = 3;
    const attempt = () => {
      const result = pending.copy();
      if (!result && --remainingAttempts) {
        setTimeout(attempt);
      } else {
        pending.destroy();
      }
    };
    attempt();

    this.showCopyIconText(index);
  }

  showCopyIconText(index: number) {
    this.contactData[index].showCopyEmailText = true;
    this.contactData[index].emailIcon = this.checkIcon;
    setTimeout(() => {
      this.contactData[index].showCopyEmailText = false;
      this.contactData[index].emailIcon = this.copyIcon;
    }, 3000);
  }

  qualtricsSurveyInitiate() {
    if (this.hasInterceptLoaded) {
      this.window.QSI?.API.unload();
      this.window.QSI?.API.load().then(() => {
        this.window.QSI?.API.run();
        this.makeSurveyResponsive();
      });
    }
  }

  makeSurveyResponsive() {
    const setHeight = () => {
      document
        .querySelectorAll(
          '#dashboard-survey-card > div, #dashboard-survey-card > div > div, #dashboard-survey-card > div > div > div, #dashboard-survey-card > div > div > div > div'
        )
        .forEach((element) => {
          const width = element.getBoundingClientRect().width;
          console.log('Element actual width:', width, 'px');

          let minHeight;
          if (width <= 346) {
            minHeight = '620px';
          } else if (width <= 340) {
            minHeight = '600px';
          } else if (width <= 480) {
            minHeight = '560px';
          } else if (width <= 530) {
            minHeight = '500px';
          } else {
            minHeight = '480px !important';
          }

          const htmlElement = element as HTMLElement;
          htmlElement.style.width = '100%';
          htmlElement.style.height = '100%';
          htmlElement.style.display = 'flex';
          htmlElement.style.flexDirection = 'column';
          htmlElement.style.minHeight = minHeight;
          htmlElement.style.zIndex = '1';
          htmlElement.style.overflow = 'hidden';
        });
    };

    // Initial set
    setHeight();

    // Update on resize
    window.addEventListener('resize', setHeight);
  }

  goContactUsPage() {
    this.router.navigate(['benefits/contacts'], { queryParamsHandling: 'preserve' });
    this.scrollToTop();
  }

  scrollToTop() {
    this.window.scrollTo(0, 0);
  }

  $hasPolicyData: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  ngOnInit(): void {
    // Get user preferences
    if (this.showToggle) {
      this.userClient.preferenceGet().subscribe({
        next: (response) => {
          this.invoiceNotificationsEnabled = Boolean(response.data?.invoiceNotification);
        },
        error: (error) => {
          console.error('Error getting user preferences:', error);
        }
      });
    }

    this.policyClient.overview().subscribe(
      (response) => {
        const { data } = response;
        if (response.data?.policyDetails === null) {
          this.$hasPolicyData.next(false);
          return;
        }
        this.overviewData = {
          ...this.overviewData,
          policyToken: data?.policyToken ?? '',
          policyNumber: data?.groupNumber ?? '',
          policyHolder: data?.policyDetails?.policyHolder ?? '',
          status: { data: data?.policyDetails?.status ?? '', label: 'Status' },
          effectiveDate: { data: this.formatDate(data?.policyDetails?.effectiveDate), label: 'Effective date' },
          nextRenewalDate: { data: this.formatDate(data?.policyDetails?.nextRenewalDate), label: 'Next renewal date' }
        };
        this.contactData = (data?.contactInfo ?? ([] as ContactInfo[]))
          .filter((x) => x.accountType.toLowerCase() !== 'broker')
          .map((rsp) => {
            const isLad = rsp.accountManagerEmail.toLowerCase() === Defaults.ladEmail.toLowerCase();
            if (isLad) return ladManagerCard;
            return {
              contactType: rsp.accountType ?? '',
              name: rsp.accountManagerName ?? '',
              email: rsp.accountManagerEmail ?? '',
              phoneNumber: rsp.accountManagerPhone ?? '',
              primaryButton: (rsp.accountType ?? '') === this.accountManager,
              showDivider: (rsp.accountType ?? '') === this.accountManager,
              showCopyEmailText: false,
              emailIcon: this.copyIcon
            };
          });

        if (this.contactData.length === 0) {
          this.hasContacts = false;
        }
      },
      () => {},
      () => {
        this.token = this.overviewData.policyToken ?? '';
        const queryParams: Params = { token: this.token };
        if (this.router) {
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams,
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }

        this.hasInterceptLoaded = true;
        this.qualtricsSurveyInitiate();
      }
    );
  }
}

export interface TooltipContent {
  body: string;
  heading: string;
}
export interface OverviewData {
  policyToken: string;
  policyNumber: string;
  policyHolder: string;
  status: DataLabel;
  effectiveDate: DataLabel;
  nextRenewalDate: DataLabel;
}

export interface ContactData {
  contactType: string;
  name: string;
  email: string;
  phoneNumber: string;
  primaryButton: boolean;
  showDivider: boolean;
  showCopyEmailText: boolean;
  emailIcon: string;
}
